import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Tabs } from 'antd'
import { z } from 'zod'
import { useTitle } from '../hooks/use-title'
import { useAuth } from '../auth'
import { NotSubscribedResult } from '../components/not-subscribed-result'
import { useClients } from '../client/hooks'
import { ClientTable } from '../client/components/client-table'
import { ClientInviteModal } from '../client/components/client-invite-modal'
import { useInvitations } from '../invitation/hooks'
import { InvitationsTable } from '../invitation/components/invitations-table'
import { CLIENT } from '../company/company-role'
import { INDEX_CLIENT, INVITE_CLIENT } from '../user/permission'
import { PageTitle } from '../components/page-title'

export const Route = createFileRoute('/_auth/_main/clients')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(INDEX_CLIENT)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    validateSearch: z.object({
        invite: z.boolean().optional(),
    }),
    component: Clients,
})

function Clients() {
    const { t } = useTranslation()

    useTitle(t('Clients'))
    const { activeCompany, can, hasSubscription } = useAuth()
    const { invite } = Route.useSearch()
    const navigate = useNavigate()

    const query = useClients(activeCompany?.id)
    const invitationsQuery = useInvitations(activeCompany?.id, CLIENT)

    const canInvite = can(INVITE_CLIENT)

    return (
        <>
            <PageTitle
                title={t('Clients')}
                extra={
                    hasSubscription &&
                    canInvite && (
                        <Link
                            to="."
                            search={{ invite: true }}
                            className="w-full sm:w-auto"
                        >
                            <Button
                                size="large"
                                type="primary"
                                block
                            >
                                {t('Invite client')}
                            </Button>
                        </Link>
                    )
                }
            />
            {!hasSubscription ? (
                <NotSubscribedResult />
            ) : (
                <Tabs
                    items={[
                        {
                            key: 'clients',
                            label: t('Clients'),
                            children: (
                                <ClientTable
                                    dataSource={query.data}
                                    loading={query.isPending}
                                />
                            ),
                        },
                        {
                            key: 'invitations',
                            label: (
                                <Badge
                                    count={invitationsQuery.data?.length}
                                    size="small"
                                    offset={[12, 0]}
                                >
                                    {t('Pending invitations')}
                                </Badge>
                            ),
                            children: (
                                <InvitationsTable
                                    companyId={activeCompany?.id}
                                    dataSource={invitationsQuery.data}
                                    loading={invitationsQuery.isPending}
                                />
                            ),
                        },
                    ]}
                />
            )}
            <ClientInviteModal
                companyId={activeCompany?.id}
                onCancel={() => {
                    navigate({ to: '.', search: {} })
                }}
                open={canInvite && invite}
            />
        </>
    )
}
